import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navOffset = 80;
      const top = element.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#a4c8ff]/80 backdrop-blur-md shadow-md' : 'bg-[#a4c8ff]/80 backdrop-blur-md'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 h-20">
            <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
              <img
                src="/images/home/header-logo.png"
                alt="ビスケット"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="text-xs sm:text-sm font-semibold text-slate-800 leading-tight">
                <span className="block">私たちはAIと</span>
                <span className="block">うまく付き合えるのか？</span>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="sm:hidden ml-auto w-10 h-10 flex items-center justify-center text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
            {/* Desktop / Tablet Menu */}
            <div className="hidden sm:flex items-center gap-4 lg:gap-6 overflow-x-auto whitespace-nowrap min-w-0 flex-1 justify-end">
              <button
                onClick={() => scrollToSection('target')}
                className="text-xs lg:text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                こんな方におすすめ
              </button>
              <button
                onClick={() => scrollToSection('program')}
                className="text-xs lg:text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                プログラム
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-xs lg:text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                受講者の声
              </button>
              <button
                onClick={() => scrollToSection('instructor')}
                className="text-xs lg:text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                講師
              </button>
              <button
                onClick={() => scrollToSection('schedule')}
                className="text-xs lg:text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                開催情報
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="sm:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-slate-200">
              <div className="px-6 py-4 space-y-3">
                <button
                  onClick={() => scrollToSection('target')}
                  className="block w-full text-left text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer py-2"
                >
                  こんな方におすすめ
                </button>
                <button
                  onClick={() => scrollToSection('program')}
                  className="block w-full text-left text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer py-2"
                >
                  プログラム
                </button>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="block w-full text-left text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer py-2"
                >
                  受講者の声
                </button>
                <button
                  onClick={() => scrollToSection('instructor')}
                  className="block w-full text-left text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer py-2"
                >
                  講師
                </button>
                <button
                  onClick={() => scrollToSection('schedule')}
                  className="block w-full text-left text-sm font-medium text-slate-700 hover:text-[#64a5ff] transition-colors cursor-pointer py-2"
                >
                  開催情報
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-24 sm:pt-32"
        style={{
          backgroundImage:
            'url(/images/home/hero-bg.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-white/60"></div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-8">
          <h1 className="font-bold text-black mb-6 leading-tight font-serif">
            <span className="block text-4xl md:text-5xl">私たちはAIとうまく付き合えるのか？</span>
            <span className="block text-2xl md:text-3xl font-medium mt-4 text-black">
              ～ビスケットを通して見える、AIを使う態度・判断・説明力～
            </span>
          </h1>
          <div className="inline-block rounded-full border border-[#64a5ff] bg-white/80 px-6 py-2 text-[#64a5ff] text-sm md:text-base font-semibold">
            1日完結｜4時間　　対面｜原宿
          </div>
          <p className="mt-4 text-slate-700 text-base font-serif">
            本講座の受付は終了しました。ご関心をお寄せいただき、ありがとうございました。
          </p>

        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-6 text-slate-700 leading-[2.6] text-left md:text-center font-serif">
            <p className="text-base leading-normal md:leading-[2]">
              仕事の場面でも、生活の場面でも、AIの存在感は日々増しています。<br />
              仕事が奪われる？ Z世代は得意？ AIを信じては危ない？ 生産性が上がる！ AI無しでは生活できない！<br />
              実際のところはどうなのでしょう。<br />
              そもそも、私たちはAIの正体を知っているのでしょうか。
            </p>

            <p className="text-base leading-normal md:leading-[2]">
              本講座では、プログラミング教育の草分け的存在、原田康徳をメイン講師に、AIという存在の解像度を上げていこうと思います。<br />
              原田が開発した「ビスケット」に加え、ChatGPT等を活用し、AIの正体を一緒に掴んでいこう！という講座です。
            </p>

            <p className="text-base leading-normal md:leading-[2]">
              正体がわかれば、仕事でも生活でも、AIとの付き合い方の最適解が見えてくるかもしれません。それはきっと人それぞれ。<br />
              みなさんの多様な意見や感想をヒントに、思考を深めたり広げることを楽しみたいと思います。
            </p>

            <p className="text-base leading-normal md:leading-[2]">
              ご参加にあたり、プログラミングの技術・知識は一切必要ありません。必要なのは、感覚のみ！でご参加いただけます。
            </p>
          </div>
        </div>
      </section>

      {/* Viscuit Section */}
      <section id="viscuit" className="pt-0 pb-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-md border border-slate-200 p-8 md:p-12">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              <span
                className="inline-block bg-gradient-to-br from-[#64a5ff] to-[#5294ee] px-1 py-2 rounded-none text-white font-serif"
                style={{ WebkitBackgroundClip: 'text', display: 'block', background: 'transparent', color: '#1e293b' }}
              >
                What is ビスケット?
              </span>
            </h2>

            <div className="grid md:grid-cols-12 gap-12 items-start">
              <div className="md:col-span-8">
                <div className="space-y-6 text-slate-700 leading-[2.6] font-serif">
                  <p className="text-base leading-normal md:leading-[2]">
                    ビスケットは、原田が2003年に開発した言葉や数値を使わないプログラミング学習ツールです。<br />
                    日常生活のなかで服を選ぶとき、食事に味付けをするとき、頼りにするのは感覚です。「もうちょっとバランスを」「やや明るめの」「塩加減はこの程度」。このような言語化、数値化が難しい感覚を、ビスケットは直感的な方法で表現し、誰でも楽しくプログラムをつくれるように、人間に寄り添ったツールです。<br />
                    そして、だからこそ、プログラミングやコンピューター、AIの本質を見つけることができるツールでもあります。
                  </p>
                </div>
              </div>

              <div className="md:col-span-4 flex items-center justify-end">
                <div className="w-full h-48 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src="/images/home/viscuit-photo.jpeg"
                    alt="ビスケットを使ったプログラミング体験"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section id="target" className="py-24 bg-gradient-to-br from-[#64a5ff] to-[#5294ee]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-[1.75rem] leading-[1.2] font-bold text-white text-center mb-16 font-serif">
            こんな方におすすめ
          </h2>

          <div className="space-y-6 max-w-2xl mx-auto md:pl-[calc(4rem-20px)] pl-0">
            {[
              { icon: 'ri-question-line', text: '職場でAI推進が声掛けられているが、どうしたらいいかわからない' },
              { icon: 'ri-emotion-unhappy-line', text: 'AIは便利そうだが、怖くて使えない' },
              { icon: 'ri-computer-line', text: 'AIに取って変わられるのが怖くて浸透してほしくないと思っている' },
              { icon: 'ri-user-search-line', text: 'AI人材って結局何を指すのかよくわからない' },
              { icon: 'ri-scales-line', text: 'AIを使いこなせる人と使いこなせない人の違いが疑問である' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/20 border border-white/40 flex items-center justify-center">
                  <i className={`${item.icon} text-white text-2xl`}></i>
                </div>
                <p className="text-lg text-white leading-relaxed font-normal" style={{ fontSize: '1.08rem' }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section id="program" className="py-24 bg-gradient-to-b from-white to-[#a4c8ff]/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-[1.75rem] leading-[1.2] font-bold text-slate-800 text-center mb-16 font-serif">
            プログラム
          </h2>

          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7">
              <div className="space-y-0">
                {[
                  'ビスケットで自己紹介',
                  'ビスケットで感覚プログラミング体験',
                  '作ったプログラムを言葉で説明したら？',
                  'AIツールに指示を出してみよう',
                  'ワークから見えてきたものを共有',
                  'ディスカッション～私たちはAIとどう関わればいいのか～',
                  '原田ハカセに質問！',
                ].map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center gap-5 py-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#64a5ff] text-white flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <p className="text-base text-slate-700">{item}</p>
                    </div>
                    {index < 6 && <div className="ml-5 border-l-2 border-dashed border-[#64a5ff]/40 h-3"></div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 space-y-4">
              <div className="w-full aspect-[10/7] rounded-2xl overflow-hidden shadow-sm">
                <img
                  src="/images/home/program-photo-1.jpeg"
                  alt="ワークショップの様子1"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full aspect-[10/7] rounded-2xl overflow-hidden shadow-sm">
                <img
                  src="/images/home/program-photo-2.jpeg"
                  alt="ワークショップの様子2"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-gradient-to-br from-[#a4c8ff]/20 to-[#64a5ff]/20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-[1.75rem] leading-[1.2] font-bold text-slate-800 text-center mb-4 font-serif">
            受講者の声
          </h2>
          <p className="text-xs text-slate-600 text-center mb-16">
            ※過去開催のオンライン版講座に参加した方の声です。
          </p>

          <div className="grid md:grid-cols-2 gap-8 mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-[#a4c8ff]/30 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-[#64a5ff] text-3xl"></i>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                プログラミング、AI双方の良さと限界を感じ取ることができた。グループの会話でも気づくことも多かった。
              </p>
              <p className="text-xs text-slate-500 font-semibold text-center">40代 会社員人事</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-[#a4c8ff]/30 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-[#64a5ff] text-3xl"></i>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                既に日常的に使用しているので、理解度の再確認と再認識になりました。講座内容はとても興味深く面白かった。
              </p>
              <p className="text-xs text-slate-500 font-semibold text-center">60代 教育関係者</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-[#a4c8ff]/30 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-[#64a5ff] text-3xl"></i>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                実際に手を動かすことで予想外のアイディアに出会い「こんなこともできるかも」という発想が広がる体験は、挑戦マインドや実験マインドが求められる多くのビジネスパーソンにこそおすすめです。
              </p>
              <p className="text-xs text-slate-500 font-semibold text-center">30代 会社員</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-[#a4c8ff]/30 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-[#64a5ff] text-3xl"></i>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                AI時代には論理的思考力があまり必要ではなくなるのでは、という感想と、自分のしたいことを言語化して論理的に説明できる能力が必要、という逆のような考えがディスカッションで出て、面白かった。
              </p>
              <p className="text-xs text-slate-500 font-semibold text-center">40代 教育関係者</p>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Section */}
      <section id="instructor" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-[1.75rem] leading-[1.2] font-bold text-slate-800 text-center mb-16 font-serif">
            講師
          </h2>

          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-3">
              <div className="w-44 sm:w-56 md:w-full mx-auto rounded-2xl overflow-hidden shadow-sm border border-slate-100">
                <img
                  src="/images/home/instructor-photo.jpeg"
                  alt="原田康徳"
                  className="w-full h-auto object-cover object-top"
                  style={{ objectPosition: 'center 15%' }}
                />
              </div>
            </div>

            <div className="md:col-span-9">
              <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center md:text-left">原田 康徳</h3>

              <p className="text-base text-slate-700 mb-6">
                ビスケット開発者／計算機科学者／博士（工学）／ワークショップデザイナー
              </p>

              <p className="text-base text-slate-700 leading-relaxed mb-6">
                コンピュータサイエンス入門講座を10年以上実施。体験と対話を通じて"本質理解"を伝えることを得意とする。2024年度日本ソフトウェア学会業績賞受賞。
              </p>

              <div className="bg-[#a4c8ff]/20 rounded-2xl p-6 flex items-start border border-[#64a5ff]/30">
                <div className="w-10 h-10 flex items-center justify-center bg-[#64a5ff] rounded-xl mr-4 flex-shrink-0">
                  <i className="ri-book-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-1">著書</p>
                  <p className="text-sm text-slate-700">
                    「さわるようにしくみがわかる コンピュータのひみつ(技術評論社)」他
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="pt-0 pb-24 bg-gradient-to-b from-white to-[#a4c8ff]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-md border-2 border-[#64a5ff]/50 p-8 md:p-12">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-calendar-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">開催日時（各回4時間／1日完結／内容は同一）</p>
                  <div className="space-y-2">
                    <p className="text-base text-slate-900"><span className="line-through">2026年3月8日（日）　13：00～17：00</span>　受付終了</p>
                    <p className="text-base text-slate-900"><span className="line-through">2026年3月28日（土）　13：00～17：00</span>　受付終了</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">参加費</p>
                  <p className="text-base text-slate-900">8,000円（税込）</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-group-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">定員</p>
                  <p className="text-base text-slate-900">12名程度</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">対象</p>
                  <p className="text-base text-slate-900">大人（プログラミング未経験者歓迎）</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-computer-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">形式</p>
                  <p className="text-base text-slate-900">対面</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-device-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">持ち物</p>
                  <p className="text-base text-slate-900 leading-relaxed">
                    ネットにつながるPC（Googleスライドへの文字入力、生成AIが使用できる端末）
                  </p>
                  <p className="text-slate-600 leading-relaxed mt-1" style={{ fontSize: '0.7rem' }}>
                    ※ビスケット用のタブレットはご用意します。<br />
                    ※会場のWifiがご利用いただけます。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-user-star-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">講師</p>
                  <p className="text-base text-slate-900">
                    原田 康徳<span style={{ fontSize: '0.7rem' }}>（ビスケット開発者／合同会社デジタルポケット代表社員）</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-team-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">ファシリテーター</p>
                  <div className="space-y-6">
                    <div className="flex flex-col gap-2">
                      <a
                        href="https://www.hellocozylife.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-[180px] h-[180px] rounded-xl overflow-hidden flex-shrink-0 block"
                      >
                        <img
                          src="/images/home/KojimaPH.jpg"
                          alt="小島 潤子"
                          className="w-full h-full object-cover"
                        />
                      </a>
                      <a
                        href="https://www.hellocozylife.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base text-slate-900 hover:text-[#5294ee] transition-colors"
                      >
                        小島 潤子<span style={{ fontSize: '0.7rem' }}>（フリーランスワークショップデザイナー）www.hellocozylife.com</span>
                      </a>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="w-[180px] h-[180px] rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src="/images/home/InouePH.jpg"
                          alt="井上 愉可里"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-base text-slate-900">
                        井上 愉可里<span style={{ fontSize: '0.7rem' }}>（合同会社デジタルポケット／青山学院大学社会情報学研究センター特別研究員）</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">会場</p>
                  <div className="space-y-4">
                    <p className="text-base text-slate-900">渋谷区神宮前1－13－23　 HRIビジョンハウス</p>
                    <div className="w-full overflow-hidden">
                      <img
                        src="/images/home/HRIvisionhouse.png"
                        alt="HRIビジョンハウス 外観"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <p className="text-sm text-slate-700">JR原宿駅・明治神宮前駅3番出口（各徒歩2分）</p>
                    <div className="w-full h-64 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1620.5!2d139.70507!3d35.67078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ca1a10b3cc1%3A0x4e1e0e6b3b8b8b8b!2z5riL6LC35Yy656We5a6u5YmNMS0xMy0yMw!5e0!3m2!1sja!2sjp!4v1700000000000"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="HRIビジョンハウス 会場地図"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-hand-heart-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">会場提供協力</p>
                  <a
                    href="https://www.hri-japan.co.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-[#64a5ff] hover:text-[#5294ee] transition-colors"
                  >
                    HRインスティテュート
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#64a5ff] rounded-full flex items-center justify-center">
                  <i className="ri-building-line text-white text-xl"></i>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">主催</p>
                  <a
                    href="https://www.viscuit.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-[#64a5ff] hover:text-[#5294ee] transition-colors"
                  >
                    合同会社デジタルポケット
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#a4c8ff]/20 to-[#64a5ff]/20 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-sm font-bold text-slate-800 mb-3">お問い合わせ</h3>
              <a href="mailto:info@viscuit.com" className="text-sm text-[#64a5ff] hover:text-[#5294ee] transition-colors">
                info@viscuit.com
              </a>
            </div>

            <div>
              <h3 className="text-sm font-bold text-slate-800 mb-3">運営｜デジタルポケット</h3>
              <a href="https://www.viscuit.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#64a5ff] hover:text-[#5294ee] transition-colors">
                www.viscuit.com
              </a>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-8 flex items-center justify-center gap-4">
            <p className="text-xs text-slate-500">©️2026 合同会社デジタルポケット</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
